/**
 * Package Summary Card
 * Displays GC/SC amounts and price in a visually appealing card
 * Used in payment checkout flows
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { GCBadgeSVG, SCBadgeSVG } from '@/components/CurrencyBadge';
import { Sparkles } from 'lucide-react';

interface PackageSummaryCardProps {
  /** Total GC amount (base + bonus) */
  totalGC: number;
  /** Total SC amount (base + bonus) */
  totalSC: number;
  /** Price in dollars */
  price: number;
  /** Optional promo code applied */
  promoCode?: string;
  /** Promo reward type */
  promoRewardType?: string;
  /** Promo reward value (percentage) */
  promoRewardValue?: number;
  /** Additional class names */
  className?: string;
  /** Whether to show the price badge */
  showPrice?: boolean;
}

// Format large numbers with commas
const formatCoins = (value: number) => value.toLocaleString();

// Get accent color based on GC tier
const getAccentColor = (gcAmount: number) => {
  if (gcAmount >= 1000000) return 'from-purple-500 via-pink-500 to-purple-500';
  if (gcAmount >= 500000) return 'from-amber-500 via-orange-400 to-amber-500';
  if (gcAmount >= 250000) return 'from-yellow-400 via-amber-400 to-yellow-400';
  if (gcAmount >= 100000) return 'from-blue-400 via-cyan-400 to-blue-400';
  if (gcAmount >= 50000) return 'from-green-400 via-emerald-400 to-green-400';
  return 'from-slate-400 via-slate-300 to-slate-400';
};

// Get glow color based on GC tier
const getGlowColor = (gcAmount: number) => {
  if (gcAmount >= 1000000) return 'shadow-purple-500/20';
  if (gcAmount >= 500000) return 'shadow-amber-500/20';
  if (gcAmount >= 250000) return 'shadow-yellow-500/20';
  if (gcAmount >= 100000) return 'shadow-blue-500/20';
  if (gcAmount >= 50000) return 'shadow-green-500/20';
  return 'shadow-slate-500/10';
};

export const PackageSummaryCard: React.FC<PackageSummaryCardProps> = ({
  totalGC,
  totalSC,
  price,
  promoCode,
  promoRewardType,
  promoRewardValue,
  className,
  showPrice = true,
}) => {
  const accentColor = getAccentColor(totalGC);
  const glowColor = getGlowColor(totalGC);

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl',
        'bg-white/5 backdrop-blur-sm',
        'border border-white/10',
        `shadow-lg ${glowColor}`,
        className
      )}
    >
      {/* Animated accent line at top */}
      <div
        className={cn(
          'absolute top-0 left-0 right-0 h-0.5',
          `bg-gradient-to-r ${accentColor}`
        )}
      />

      <div className="relative p-4 lg:p-5">
        {/* Currency display */}
        <div className={cn("flex items-center justify-center gap-3 lg:gap-4", showPrice && "mb-3 lg:mb-4")}>
          {/* GC Amount */}
          {totalGC > 0 && (
            <div className="flex items-center gap-1.5 lg:gap-2">
              <GCBadgeSVG size={22} />
              <span className="text-lg lg:text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-amber-400">
                {formatCoins(totalGC)}
              </span>
            </div>
          )}

          {/* Plus separator */}
          {totalGC > 0 && totalSC > 0 && (
            <span className="text-muted-foreground/60 font-medium">+</span>
          )}

          {/* SC Amount */}
          {totalSC > 0 && (
            <div className="flex items-center gap-1.5 lg:gap-2">
              <SCBadgeSVG size={22} />
              <div className="flex items-center gap-1 lg:gap-1.5">
                <span className="text-base lg:text-lg font-black text-transparent bg-clip-text bg-gradient-to-r from-green-300 to-emerald-400">
                  {formatCoins(totalSC)}
                </span>
                <span className="text-[9px] lg:text-[10px] font-bold text-green-400 uppercase tracking-wider bg-green-500/20 px-1.5 py-0.5 rounded">
                  Free
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Price badge */}
        {showPrice && (
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 px-4 lg:px-5 py-1.5 lg:py-2 rounded-xl bg-white/10 border border-white/10">
              <Sparkles className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-amber-400" />
              <span className="text-xl lg:text-2xl font-black text-foreground">
                ${price.toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {/* Promo code section */}
        {promoCode && (
          <div className="mt-3 lg:mt-4 pt-2.5 lg:pt-3 border-t border-white/10">
            <div className="flex items-center justify-center gap-2 text-[10px] lg:text-xs">
              <span className="text-muted-foreground">
                Promo: <span className="font-bold text-amber-400">{promoCode}</span>
              </span>
              {promoRewardType === 'discount' && promoRewardValue && (
                <span className="text-green-400 font-bold px-2 py-0.5 bg-green-500/20 rounded">
                  {promoRewardValue}% OFF
                </span>
              )}
              {promoRewardType === 'bonus' && promoRewardValue && (
                <span className="text-green-400 font-bold px-2 py-0.5 bg-green-500/20 rounded">
                  +{promoRewardValue}% Bonus
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
